import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ChevronRight, MessageCircle } from "lucide-react";
import api, { waLink } from "../lib/api";
import { Reveal, Eyebrow } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryModal";
import { useTitle } from "../hooks/useTitle";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useTitle(service ? service.name : "Service");

  useEffect(() => {
    setService(null);
    setNotFound(false);
    api.get(`/services/${slug}`).then((r) => setService(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="py-32 text-center" data-testid="service-not-found">
        <h1 className="font-display font-extrabold text-2xl text-[#0A192F]">Service not found</h1>
        <Link to="/services" className="text-[#0F5132] font-bold text-sm mt-4 inline-block">← Back to services</Link>
      </div>
    );
  if (!service) return <div className="py-32 text-center text-sm text-[#627D98]">Loading service...</div>;

  return (
    <div data-testid="service-detail-page">
      <section className="bg-[#0A192F] relative overflow-hidden py-14 lg:py-20">
        <div className="absolute inset-0 hero-grid" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-6" data-testid="service-breadcrumbs">
            <Link to="/" className="hover:text-[#C59B27]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/services" className="hover:text-[#C59B27]">Services</Link>
            <ChevronRight size={12} />
            <span className="text-[#C59B27]">{service.name}</span>
          </nav>
          <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl max-w-3xl leading-[1.1]">
            {service.name}
          </h1>
          <p className="mt-5 text-slate-300 text-base max-w-2xl leading-relaxed">{service.short_desc}</p>
        </div>
      </section>
      <section className="py-14 lg:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_380px] gap-12">
        <div>
          <Reveal>
            <Eyebrow>Overview</Eyebrow>
            <p className="mt-4 text-base text-[#334E68] leading-relaxed">{service.description}</p>
          </Reveal>
          {service.deliverables && service.deliverables.length > 0 && (
            <Reveal className="mt-10">
              <Eyebrow>What's included</Eyebrow>
              <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="service-deliverables">
                {service.deliverables.map((d, i) => (
                  <div key={i} className="flex items-start gap-2.5 bg-white border border-slate-200 rounded-xl p-4">
                    <CheckCircle2 size={16} className="text-[#0F5132] shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-[#334E68]">{d}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          )}
        </div>
        <aside className="lg:sticky lg:top-24 self-start space-y-5">
          <div className="bg-white rounded-2xl border-2 border-[#C59B27]/60 p-6 shadow-lg" data-testid="service-enquiry-card">
            <h3 className="font-display font-extrabold text-lg text-[#0A192F]">Discuss Your Requirement</h3>
            <p className="text-xs text-[#627D98] mt-1 mb-5">Tell us what you need — we'll respond within one business day.</p>
            <EnquiryForm defaultInterest={service.name} source={`service-page-${slug}`} />
          </div>
          <a
            href={waLink(`Hi, I am interested in your ${service.name} service. Please share the details.`)}
            target="_blank"
            rel="noreferrer"
            data-testid="service-whatsapp-btn"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-[#25D366] text-white font-display font-bold text-sm hover:bg-[#1fb857] transition-colors"
          >
            <MessageCircle size={17} /> Ask on WhatsApp
          </a>
        </aside>
      </section>
    </div>
  );
}
